import React, { useContext, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { VerticalPricingCalculator } from "./VerticalPricingCalculator";

import { PricingContainer } from "./PricingContainer";
import theme from "../../theme";
import { IDesign, Translations } from "../../types";
import { Material } from "./types";
import {
  ThemeProvider,
  Dialog,
  CssBaseline,
  IconButton,
  useTheme,
} from "@mui/material";
import StartDesign from "./StartDesign";
import { SnackbarProvider, enqueueSnackbar, closeSnackbar } from "notistack";
import { APIContext } from "../../contexts/apiContext";
import useWebsiteApi from "../../hooks/useWebsiteApi";
import { Close as CloseIcon } from "@mui/icons-material";
import "../../bugsnag";
import ErrorView from "../ErrorView";
import { ErrorBoundary } from "../../bugsnag";
import { useProduct } from "./useProduct";
import { SubmitType } from "./sections/Footer";
import useShopifyApi from "../../hooks/useShopifyApi";
import Bugsnag from "@bugsnag/js";

const fallbackDefaultQuantities = [50, 100, 200, 500, 1000, 2000, 5000, 10000];
const graphicModeQuantities = [1, 5, 10, 100, 1000];
const fallbackDefaultQuantity = 50;
const graphicModeDefaultQuantity = 1;

const forceInchSkus = [
  "STICKER-CUSTOM-CIRCULAR-0.5",
  "STICKER-CUSTOM-CIRCULAR-1",
  "STICKER-CUSTOM-CIRCULAR-2",
  "STICKER-CUSTOM-CIRCULAR-3",
  "STICKER-CUSTOM-CIRCULAR-4",
  "STICKER-CUSTOM-CIRCULAR-5",
];

const addTranslations = (
  translations: Translations["Materials"],
  materials: Material[]
): Material[] => {
  return materials.map((m) => {
    return {
      ...m,
      name: translations[m.sku]?.name || m.sku,
      description: translations[m.sku]?.description || "",
      bestseller: translations[m.sku]?.bestseller || "",
    };
  });
};

export const main = (translations: Translations) => {
  const container = document.getElementById("loader-pc");
  const baseUrl = container?.getAttribute("data-pc-api-url")?.valueOf() || "";
  const locale =
    container?.getAttribute("data-pc-locale")?.valueOf().replace("_", "-") ||
    "";
  const storeHash =
    container?.getAttribute("data-pc-store-hash")?.valueOf() || "";
  const initialproductSku =
    container?.getAttribute("data-pc-product-sku")?.valueOf() || "";
  const cartPath =
    container?.getAttribute("data-pc-cart-path")?.valueOf() || "";
  const materialEnabled =
    container?.getAttribute("data-pc-material-enabled")?.valueOf() === "true";
  const dimensionsEnabled =
    container?.getAttribute("data-pc-dimensions-enabled")?.valueOf() !==
    "false";
  const shapeEnabled =
    container?.getAttribute("data-pc-shape-enabled")?.valueOf() === "true";
  const defaultShape = container
    ?.getAttribute("data-pc-default-shape")
    ?.valueOf();
  const quantityEnabled =
    container?.getAttribute("data-pc-quantity-enabled")?.valueOf() === "true";

  const graphicMode =
    container?.getAttribute("data-pc-graphic-mode") === "true";
  const graphicTemplateId = container?.getAttribute(
    "data-pc-graphic-template-id"
  );
  const fallbackQuantity = graphicMode
    ? graphicModeDefaultQuantity
    : fallbackDefaultQuantity;
  const fallbackQuantities = graphicMode
    ? graphicModeQuantities
    : fallbackDefaultQuantities;
  const fixedAspectRatio = graphicMode;
  const productSku = graphicMode ? "STICKER-CUSTOM-DIE" : initialproductSku;
  const variantId = productSku.startsWith("SAMPLE-")
    ? parseInt(
        container?.getAttribute("data-pc-variant-id")?.valueOf() || ""
      ) || undefined
    : undefined;
  const fixedQuantity =
    parseInt(
      container?.getAttribute("data-pc-fixed-quantity")?.valueOf() || ""
    ) || fallbackQuantity;

  const root = createRoot(container);

  if (!container) {
    return;
  }

  const defaultUnit =
    Shopify.country === "US" || forceInchSkus.includes(productSku)
      ? "in"
      : "cm";

  const Wrapped = () => {
    const theme = useTheme();
    const websiteApi = useWebsiteApi();
    const shopifyApi = useShopifyApi();
    const { currencyCode, shipToCountryCode, locale } = useContext(APIContext);
    const [design, setDesign] = useState<IDesign>({
      quantity: fixedQuantity,
      width: 2,
      height: 2,
      unit: defaultUnit,
      materialSku: "vinyl-white",
      productSku: productSku,
      shape: "custom",
      variantId,
    });
    const product = useProduct(
      design.productSku,
      design.unit,
      (graphicMode && graphicTemplateId) || undefined
    );
    const [quote, setQuote] = useState(null);
    const prices = quote?.quantities
      ? quote.quantities
      : fallbackQuantities.map((qty) => {
          return {
            quantity: qty,
            price: 0,
            currencyCode: "GBP",
            saving: 0,
            isLoading: true,
          };
        });
    const showInches = Shopify.country === "US";

    const materials = useMemo(() => {
      return addTranslations(
        translations.Materials,
        product?.materials || []
      ).sort((a, b) => {
        if (a.sortOrder && b.sortOrder) {
          return b.sortOrder - a.sortOrder;
        }

        return b.name.localeCompare(a.name);
      });
    }, [product?.materials]);
    const [startDesignOpen, setStartDesignOpen] = useState(false);
    const [touched, setTouched] = useState(false);

    useEffect(() => {
      if (!product || touched) {
        return;
      }

      const toDim = (n: number, unit: string): number => {
        switch (unit) {
          case "mm":
            return n / 10;
        }

        return n;
      };

      setDesign((d) => {
        const newDesign = {
          ...d,
          width: toDim(
            product.defaultDimensions.width,
            product.defaultDimensions.unit
          ),
          height: toDim(
            product.defaultDimensions.height,
            product.defaultDimensions.unit
          ),
          materialSku: materials?.length > 0 ? materials[0].sku : "vinyl-white",
          shape: defaultShape || product.shapes[0].shape || "custom",
        };

        return newDesign;
      });
      setTouched(true);
    }, [product, touched, materials]);

    // TODO: remove once useState calls have been removed in VerticalPricingCalculator
    if (!touched) {
      return null;
    }

    return (
      <>
        <Dialog
          onClose={() => {
            setStartDesignOpen(false);
          }}
          open={startDesignOpen}
          fullScreen={true}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 20,
              right: 24,
              width: "24px",
              height: "24px",
              fontSize: "18px",
              backgroundColor: theme.colors.greyLight,
            }}
            size="small"
            onClick={() => {
              setStartDesignOpen(false);
            }}
          >
            <CloseIcon sx={{ color: "white" }} fontSize="inherit" />
          </IconButton>
          <StartDesign
            translations={translations.StartDesign}
            design={design}
            cartPath={cartPath}
          />
        </Dialog>

        <VerticalPricingCalculator
          graphicMode={graphicMode}
          dimensionsEnabled={dimensionsEnabled}
          materialEnabled={materialEnabled}
          shapeEnabled={shapeEnabled}
          quantityEnabled={quantityEnabled}
          fixedQuantity={fixedQuantity}
          allTranslations={translations}
          currencyCode={currencyCode}
          locale={locale}
          defaultMaterialSku={materials?.[0]?.sku || "vinyl-white"}
          materials={materials}
          shape={design?.shape || "custom"}
          shapes={product?.shapes || []}
          price={{
            quantity: design.quantity,
            currencyCode: currencyCode,
            price: quote?.priceIncTax || null,
          }}
          prices={prices}
          pricesIncTax={true}
          moq={quote?.moq || 1}
          onChangeComplete={(
            width,
            height,
            quantity,
            materialSku,
            shape,
            unit
          ) => {
            if (!touched) {
              return;
            }

            const params = new URLSearchParams();
            params.set("w", width.toString());
            params.set("h", height.toString());
            params.set("u", unit);
            params.set("quantity", quantity.toString());
            params.set("productSku", productSku);
            params.set("materialSku", materialSku);
            params.set("currencyCode", currencyCode);
            params.set("shipToCountryCode", shipToCountryCode);
            params.set("languageCode", locale);

            if (graphicMode) {
              graphicModeQuantities.forEach((qty) => {
                params.append("qtys", qty.toString());
              });
            }

            websiteApi.quote(params).then((quote) => {
              setQuote(quote);

              setDesign((d) => {
                return {
                  ...d,
                  productSku,
                  quantity,
                  width,
                  height,
                  unit,
                  materialSku,
                  shape,
                };
              });
            });
          }}
          onSubmit={async (stype: SubmitType) => {
            switch (stype) {
              case SubmitType.Customize:
                const params = new URLSearchParams();
                params.set("u", "cm");
                params.set("w", (design.width * 10).toString());
                params.set("h", (design.height * 10).toString());
                params.set("qty", design.quantity.toString());
                if (graphicTemplateId) {
                  params.set("templateId", graphicTemplateId);
                }

                window.location.href = `https://graphic.stickerit.co/design/new?${params.toString()}`;
                return;
              case SubmitType.Continue:
                setStartDesignOpen(true);
                break;
              case SubmitType.AddToCart:
                const key = enqueueSnackbar("Adding to cart", {
                  variant: "info",
                  autoHideDuration: 30000,
                });
                // setAddingToCart(true);

                try {
                  const {
                    data: { project_id },
                  } = await websiteApi.createProject(graphicTemplateId || "");
                  await shopifyApi.addToCart(design, [
                    { id: `graphic://${project_id}`, contentType: "", src: "" },
                  ]);
                  window.location.pathname = cartPath;
                } catch (err) {
                  enqueueSnackbar("Error adding to the cart", {
                    variant: "error",
                  });
                  Bugsnag.notify(err);
                  console.error(err);
                } finally {
                  // setAddingToCart(false);
                  closeSnackbar(key);
                }
                break;
            }
          }}
          units={design?.unit || "cm"}
          minDimensions={
            product?.minDimensions || { unit: "cm", width: 1, height: 1 }
          }
          maxDimensions={
            product?.maxDimensions || { unit: "cm", width: 33, height: 70 }
          }
          quantity={design?.quantity || fallbackQuantity}
          width={design?.width || product?.minDimensions?.width || 2}
          height={design?.height || product?.minDimensions?.height || 2}
          showInches={showInches}
          productSku={design.productSku}
          fixedAspectRatio={fixedAspectRatio}
          // {...(props as IProps)}
          // customer={siCustomerInfo}
        />
      </>
    );
  };

  root.render(
    <ErrorBoundary FallbackComponent={ErrorView}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SnackbarProvider
          domRoot={document.querySelector("body")}
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
        />
        <APIContext.Provider
          value={{
            baseUrl,
            currencyCode: Shopify.currency.active,
            shipToCountryCode: Shopify.country,
            locale: locale,
            storeHash,
          }}
        >
          <PricingContainer customWidthHeight={{}}>
            <Wrapped />
          </PricingContainer>
        </APIContext.Provider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
